/**
 * LiveAvatar avatar catalog for video call mode.
 *
 * Replaces the old HeyGen Interactive Avatar list. HeyGen stock avatars were
 * addressable by stable public names ("Graham_Chair_Sitting_public"), so a
 * hardcoded list worked. LiveAvatar avatars are per-workspace UUIDs, so there is
 * nothing meaningful to hardcode — the catalog has to come from the API.
 */

export interface LiveAvatarConfig {
  id: string;
  name: string;
  /** LiveAvatar avatar UUID, passed to the session-token endpoint. */
  avatarId: string;
  description: string;
  previewImage?: string;
}

/**
 * Stand-in used before the live catalog resolves, and when it fails.
 *
 * `avatarId` is empty on purpose: the session hook refuses to start against a
 * blank ID, so a failed catalog fetch surfaces as a clear error rather than a
 * confusing rejection from LiveAvatar about an unknown avatar.
 */
export const PLACEHOLDER_AVATAR: LiveAvatarConfig = {
  id: '',
  name: 'No avatars available',
  avatarId: '',
  description: 'Connect a LiveAvatar workspace to load avatars',
};

/** Keeps settings types non-nullable; never contains a usable avatar. */
export const liveAvatars: LiveAvatarConfig[] = [PLACEHOLDER_AVATAR];

/** Raw shape returned by LiveAvatar's /v1/avatars and /v1/avatars/public. */
export interface LiveAvatarApiItem {
  id: string;
  name?: string;
  preview_url?: string;
  status?: string;
  type?: string;
  is_expired?: boolean;
  is_public?: boolean;
  default_voice?: { id?: string; name?: string };
}

/** Map the API response into the config shape the roleplay UI uses. */
export const mapLiveAvatars = (items: LiveAvatarApiItem[]): LiveAvatarConfig[] =>
  items
    .filter(
      (item) =>
        !!item.id &&
        !item.is_expired &&
        (item.status ?? 'ACTIVE').toUpperCase() === 'ACTIVE'
    )
    .map((item) => ({
      id: item.id,
      name: item.name?.trim() || 'Untitled avatar',
      avatarId: item.id,
      description: item.is_public ? 'LiveAvatar preset' : 'Your workspace avatar',
      previewImage: item.preview_url,
    }))
    // Workspace avatars first — a paid workspace should see its own faces at the
    // top — then presets, each A–Z.
    .sort((a, b) => {
      const aPreset = a.description === 'LiveAvatar preset' ? 1 : 0;
      const bPreset = b.description === 'LiveAvatar preset' ? 1 : 0;
      return aPreset - bPreset || a.name.localeCompare(b.name);
    });

export const getLiveAvatar = (id: string): LiveAvatarConfig | undefined =>
  liveAvatars.find((a) => a.id === id);

export const DEFAULT_LIVE_AVATAR = liveAvatars[0];

/** LocalStorage key for the user's selected avatar. */
export const LIVE_AVATAR_STORAGE_KEY = 'bigeSalesCoach_liveAvatarId';
